import { InAppNotification } from 'types/InAppNotification';

const PostInAppNotification = {
  PostComment: 1,
  PostAssignTask: 2,
  PostToApprove: 3,
  PostTaskMarkedAsDone: 4,
  PostApproved: 5,
  PostRejected: 6,
  PostCommentMention: 7,
  PostInstagramScheduleReminder: 8,
} as const;

const VideoInAppNotification = {
  VideoUploadSuccess: 9,
  VideoUploadFail: 10,
} as const;

const GlobalPostInAppNotification = {
  GlobalPostUpdated: 12,
  GlobalPostDeleted: 13,
  GlobalPostApproved: 14,
  LocalPostToDelete: 15,
} as const;

const GeneralInAppNotification = {
  WelcomeToApp: 20,
  MyStats: 21,
} as const;

export const PageConnectionInAppNotification = {
  PostFailedToPublish: 130,
  PageConnectionRevoked: 131,
  PageConnectionExpiredPostsAtRisk: 132,
  PageSingleTokenRecommendation: 133,
  PageConnectionExpiringSoon: 134,
  PageConnectionPermissionDowngraded: 135,
  PageConnectionRestored: 136,
} as const;

const URGENT_IN_APP_NOTIFICATION_TYPES: number[] = [
  PageConnectionInAppNotification.PostFailedToPublish,
  PageConnectionInAppNotification.PageConnectionExpiredPostsAtRisk,
];

// Amber/info regardless of payload. Type 135 is intentionally excluded here
// — its severity depends on postsAtRisk, handled separately below. Type 133
// is intentionally excluded too — it stays 'default' (see plan Self-Review).
const INFO_IN_APP_NOTIFICATION_TYPES: number[] = [
  PageConnectionInAppNotification.PageConnectionRevoked,
  PageConnectionInAppNotification.PageConnectionExpiringSoon,
];

// Green/positive — the one page-connection type that reports good news.
const SUCCESS_IN_APP_NOTIFICATION_TYPES: number[] = [
  PageConnectionInAppNotification.PageConnectionRestored,
];

export function isUrgentInAppNotificationType(type: number): boolean {
  return URGENT_IN_APP_NOTIFICATION_TYPES.includes(type);
}

export type NotificationSeverity = 'urgent' | 'info' | 'success' | 'default';

export function getNotificationSeverity(
  notification: InAppNotification,
): NotificationSeverity {
  if (isUrgentInAppNotificationType(notification.type)) {
    return 'urgent';
  }

  if (
    notification.type ===
    PageConnectionInAppNotification.PageConnectionPermissionDowngraded
  ) {
    return sumPostsAtRisk(notification.pages ?? []) > 0 ? 'urgent' : 'info';
  }

  if (SUCCESS_IN_APP_NOTIFICATION_TYPES.includes(notification.type)) {
    return 'success';
  }

  if (INFO_IN_APP_NOTIFICATION_TYPES.includes(notification.type)) {
    return 'info';
  }

  return 'default';
}

export function isUrgentInAppNotification(
  notification: InAppNotification,
): boolean {
  return getNotificationSeverity(notification) === 'urgent';
}

export type NotificationPage = {
  name: string;
  socialNetwork: string;
  postsAtRisk?: number;
};

// Page names come from the social networks and are rendered via
// dangerouslySetInnerHTML — they must never carry markup through.
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function summarizePageNames(
  pages: NotificationPage[],
  fallback: string,
): string {
  const names = pages
    .map((page) => (typeof page.name === 'string' ? escapeHtml(page.name) : ''))
    .filter(Boolean);

  if (names.length === 0) {
    return fallback;
  }

  if (names.length > 3) {
    return `${names.slice(0, 3).join(', ')} +${names.length - 3}`;
  }

  return names.join(', ');
}

function sumPostsAtRisk(pages: NotificationPage[]): number {
  return pages.reduce((sum, page) => sum + (page.postsAtRisk ?? 0), 0);
}

function postNotificationMessageHTML(notification: {
  type: number;
  author: string;
  pageName: string;
  pageCount?: number;
}): string {
  switch (notification.type) {
    case PostInAppNotification.PostComment:
      return `<div><strong>${notification.author}</strong> commented your post within <strong>${notification.pageName}</strong>.</div>`;
    case PostInAppNotification.PostAssignTask:
      return `<div><strong>${notification.author}</strong> assigned you a task within <strong>${notification.pageName}</strong>.</div>`;
    case PostInAppNotification.PostToApprove: {
      const within =
        (notification.pageCount ?? 1) > 1
          ? 'multiple profiles'
          : notification.pageName;
      return `<div><strong>${notification.author}</strong> sent you posts for approval within <strong>${within}</strong>.</div>`;
    }
    case PostInAppNotification.PostTaskMarkedAsDone:
      return `<div><strong>${notification.author}</strong> completed a task within <strong>${notification.pageName}</strong>.</div>`;
    case PostInAppNotification.PostApproved:
      return `<div><strong>${notification.author}</strong> approved your post within <strong>${notification.pageName}</strong>.</div>`;
    case PostInAppNotification.PostRejected:
      return `<div><strong>${notification.author}</strong> marked your post as REWORK within <strong>${notification.pageName}</strong>.</div>`;
    case PostInAppNotification.PostCommentMention:
      return `<div><strong>${notification.author}</strong> mentioned you in a comment within <strong>${notification.pageName}</strong>.</div>`;
    case PostInAppNotification.PostInstagramScheduleReminder:
      return `<div>Reminder to publish an Instagram post within <strong>${notification.pageName}</strong>.</div>`;
    default:
      return '';
  }
}

function videoNotificationMessageHTML(notification: { type: number }): string {
  switch (notification.type) {
    case VideoInAppNotification.VideoUploadSuccess:
      return `<div>A video has <strong>finished</strong> upload</div>`;
    case VideoInAppNotification.VideoUploadFail:
      return `<div>A video has <strong>failed</strong> to upload</div>`;
    default:
      return '';
  }
}

function globalPostNotificationMessageHTML(notification: {
  type: number;
  pageName: string;
}): string {
  switch (notification.type) {
    case GlobalPostInAppNotification.GlobalPostUpdated:
      return `<div><strong>Global post</strong> has been <strong>updated</strong> in <strong>${notification.pageName}</strong>. There are pending post changes.</div>`;
    case GlobalPostInAppNotification.GlobalPostDeleted:
      return `<div>Global content plan changed. The <strong>post was removed</strong> by an admin within <strong>${notification.pageName}</strong></div>`;
    case GlobalPostInAppNotification.GlobalPostApproved:
      return `<div>A <strong>new post</strong> from a global content plan is ready in <strong>${notification.pageName}</strong></div>`;
    case GlobalPostInAppNotification.LocalPostToDelete:
      return `<div>Global content plan changed. You should <strong>exclude</strong> a post within <strong>${notification.pageName}</strong></div>`;
    default:
      return '';
  }
}

function generalNotificationMessageHTML(notification: {
  type: number;
  author: string;
}): string {
  switch (notification.type) {
    case GeneralInAppNotification.WelcomeToApp:
      return `<div>Hi <strong>${notification.author}</strong>, welcome to <strong>Kontentino</strong>. We are happy to have you here.</div>`;
    case GeneralInAppNotification.MyStats:
      return `<div>Congrats <strong>${notification.author}</strong>, you're among <strong>the 30% of our most productive users</strong>! Check how much time you spent in Kontentino last month.</div>`;
    default:
      return '';
  }
}

function pageConnectionNotificationMessageHTML(notification: {
  type: number;
  pageName: string;
  pageCount?: number;
  pages?: NotificationPage[];
}): string {
  const pages = notification.pages ?? [];
  const names = summarizePageNames(
    pages,
    escapeHtml(notification.pageName) || 'your profile',
  );

  switch (notification.type) {
    case PageConnectionInAppNotification.PostFailedToPublish: {
      const within =
        (notification.pageCount ?? 1) > 1
          ? 'multiple profiles'
          : escapeHtml(notification.pageName) || names;
      return `<div>A post <strong>failed to publish</strong> within <strong>${within}</strong>.</div>`;
    }
    case PageConnectionInAppNotification.PageConnectionRevoked:
      return `<div>Connection to <strong>${names}</strong> was revoked — <strong>reconnect</strong> to keep publishing.</div>`;
    case PageConnectionInAppNotification.PageConnectionExpiredPostsAtRisk: {
      const postsAtRisk = sumPostsAtRisk(pages);

      if (postsAtRisk > 0) {
        const postsLabel =
          postsAtRisk === 1 ? 'scheduled post' : 'scheduled posts';
        return `<div><strong>${names}</strong> lost connection — <strong>${postsAtRisk} ${postsLabel}</strong> at risk.</div>`;
      }

      return `<div><strong>${names}</strong> lost connection — <strong>reconnect</strong> to keep publishing.</div>`;
    }
    case PageConnectionInAppNotification.PageSingleTokenRecommendation:
      // DRAFT COPY — pending Vlado's wording sign-off (see
      // docs/superpowers/plans/2026-07-16-gap-resolution.md, Gap 3).
      return `<div><strong>${names}</strong> relies on a single connection. Add a backup — your own or a teammate's — to keep publishing safe.</div>`;
    case PageConnectionInAppNotification.PageConnectionExpiringSoon:
      return `<div>Your connection to <strong>${names}</strong> is expiring soon — <strong>reconnect</strong> to keep publishing.</div>`;
    case PageConnectionInAppNotification.PageConnectionPermissionDowngraded: {
      const postsAtRisk = sumPostsAtRisk(pages);

      if (postsAtRisk > 0) {
        const postsLabel =
          postsAtRisk === 1 ? 'scheduled post' : 'scheduled posts';
        return `<div><strong>${names}</strong> lost a required permission — <strong>reconnect</strong> to keep publishing. <strong>${postsAtRisk} ${postsLabel}</strong> at risk.</div>`;
      }

      return `<div><strong>${names}</strong> lost a required permission — <strong>reconnect</strong> to keep publishing.</div>`;
    }
    case PageConnectionInAppNotification.PageConnectionRestored:
      return `<div>Connection to <strong>${names}</strong> restored — your posts are safe.</div>`;
    default:
      return '';
  }
}

export function notificationMessageHTML(notification: {
  type: number;
  author: string;
  pageName: string;
  pageCount?: number;
  pages?: NotificationPage[];
}): string {
  if (
    Object.values(PostInAppNotification).includes(notification.type as never)
  ) {
    return postNotificationMessageHTML({
      type: notification.type,
      author: notification.author,
      pageName: notification.pageName,
      pageCount: notification.pageCount,
    });
  }

  if (
    Object.values(VideoInAppNotification).includes(notification.type as never)
  ) {
    return videoNotificationMessageHTML({
      type: notification.type,
    });
  }

  if (
    Object.values(GlobalPostInAppNotification).includes(
      notification.type as never,
    )
  ) {
    return globalPostNotificationMessageHTML({
      type: notification.type,
      pageName: notification.pageName,
    });
  }

  if (
    Object.values(GeneralInAppNotification).includes(notification.type as never)
  ) {
    return generalNotificationMessageHTML({
      type: notification.type,
      author: notification.author,
    });
  }

  if (
    Object.values(PageConnectionInAppNotification).includes(
      notification.type as never,
    )
  ) {
    return pageConnectionNotificationMessageHTML({
      type: notification.type,
      pageName: notification.pageName,
      pageCount: notification.pageCount,
      pages: notification.pages,
    });
  }

  return '<div>You have a new notification</div>';
}
